import { isValidElement, useId } from "react";
import { cn } from "@/lib/utils";

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const sideStyles = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

// Elements/components that are already keyboard-focusable on their own —
// the wrapper shouldn't add a redundant tab stop in front of them.
const NATIVE_FOCUSABLE_TAGS = new Set(["button", "a", "input", "select", "textarea"]);

function isAlreadyFocusable(children: React.ReactNode): boolean {
  if (!isValidElement(children)) return false;
  if (typeof children.type === "string") {
    return NATIVE_FOCUSABLE_TAGS.has(children.type);
  }
  // Custom components (e.g. <Button>) — assume focusable unless explicitly
  // marked disabled, since most of our UI primitives render a native button/anchor.
  const props = children.props as Record<string, unknown>;
  return !props?.disabled;
}

export function Tooltip({
  content,
  children,
  side = "top",
  className,
}: TooltipProps) {
  const tooltipId = useId();
  const childAlreadyFocusable = isAlreadyFocusable(children);

  return (
    <span
      tabIndex={childAlreadyFocusable ? undefined : 0}
      aria-describedby={tooltipId}
      className={cn(
        "group relative inline-flex",
        !childAlreadyFocusable &&
          "focus:outline-none focus-visible:ring-1 focus-visible:ring-teal focus-visible:rounded-sm",
        className
      )}
    >
      {children}
      <span
        id={tooltipId}
        role="tooltip"
        className={cn(
          "pointer-events-none absolute z-50 hidden w-max max-w-xs rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs text-white shadow-md group-hover:block group-focus-within:block",
          sideStyles[side]
        )}
      >
        {content}
      </span>
    </span>
  );
}
